import { LocalDate } from '@js-joda/core';
import { useEffect, useMemo } from 'react';

import { VisitSettlement } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { FiscalDocument } from '@dreryk/egabinet-invoice-frontend-api';
import { Patient } from '@dreryk/egabinet-patient-frontend-api';
import { TaxRate } from '@dreryk/egabinet-private-medical-service-frontend-api';
import { TaxIdentifier } from '@dreryk/tax-identifier-common-api';

import { useFormController } from '@dreryk/react-common';
import { Form, StyledFlex } from '@dreryk/react-components';

import { InvoiceCreateFormAddressSection } from './addressSection/InvoiceCreateFormAddressSection';
import { InvoiceCreateFormCompanyIdentificationDetails } from './companySection/InvoiceCreateFormCompanyIdentificationDetails';
import { InvoiceCreateFormBean, InvoiceCreateFormController } from './InvoiceCreateForm.types';
import {
   fiscalDocumentTypeVisitor,
   getCompanyDefaultValues,
   getInvoiceCreateFormInitialValues,
   getPrivateInvidualDefaultValues,
} from './InvoiceCreateForm.utils';
import { InvoiceFor } from './InvoiceCreateFormSwitch.types';
import { invoiceCreateFormInvoiceForVisitor } from './InvoiceCreateFormSwitch.utils';
import { InvoiceCreateFormPrivateIndividualSection } from './privateIndividualSection/InvoiceCreateFormPrivateIndividualSection';
import { InvoiceCreateFormServicesSection } from './servicesSection/InvoiceCreateFormServicesSection';

type InvoiceCreateFormRenderProps = Pick<InvoiceCreateFormController, 'handleSubmit' | 'formState'>;

type InvoiceCreateFormRenderer = (renderProps: InvoiceCreateFormRenderProps) => JSX.Element;

type InvoiceCreateFormProps = {
   readonly children: InvoiceCreateFormRenderer;
   readonly fiscalDocuments: FiscalDocument[];
   readonly invoiceFor: InvoiceFor;
   readonly patient: Patient;
   readonly taxRates: TaxRate[];
   readonly visitSettlement: VisitSettlement;
   readonly visitDate: LocalDate;
   readonly fiscalPrintEnabled: boolean;
   readonly taxIdentifier: TaxIdentifier | undefined;
};

// eslint-disable-next-line max-lines-per-function
export const InvoiceCreateForm = (props: InvoiceCreateFormProps): JSX.Element => {
   const {
      children,
      invoiceFor,
      patient,
      taxRates,
      visitSettlement,
      visitDate,
      fiscalPrintEnabled,
      fiscalDocuments,
      taxIdentifier,
   } = props;

   const {
      handleSubmit,
      formState,
      getValue,
      getValues,
      register,
      watch,
      reset,
      trigger,
   } = useFormController<InvoiceCreateFormBean>(getInvoiceCreateFormInitialValues(patient, visitSettlement));

   const { companyName, country, invoiceItems } = watch(['companyName', 'country', 'invoiceItems']);

   useEffect(() => {
      invoiceCreateFormInvoiceForVisitor(invoiceFor)({
         visitCompany: () =>
            reset({
               ...getValues(),
               ...getCompanyDefaultValues(),
               taxIdentifier:
                  taxIdentifier?.toString() ??
                  fiscalDocuments
                     .find((fiscalDocument) => 'taxIdentifier' in fiscalDocument)
                     ?.taxIdentifier?.toString() ??
                  undefined,
            }),
         visitPrivateIndividual: () =>
            reset({
               ...getValues(),
               ...getPrivateInvidualDefaultValues(patient),
            }),
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [invoiceFor]);

   const receiptAvailableForInvoice = useMemo(
      () =>
         fiscalPrintEnabled &&
         fiscalDocumentTypeVisitor(fiscalDocuments)({
            receiptWithTaxIdentifier: () => true,
            receiptWithoutTaxIdentifier: () => true,
            noFiscalDocuments: () => false,
         }),
      [fiscalDocuments, fiscalPrintEnabled]
   );

   return (
      <Form>
         <StyledFlex gap={32} direction={'column'}>
            {invoiceCreateFormInvoiceForVisitor(invoiceFor)({
               visitCompany: () => (
                  <>
                     <InvoiceCreateFormCompanyIdentificationDetails
                        companyName={companyName}
                        fiscalDocuments={fiscalDocuments}
                        taxIdentifier={taxIdentifier}
                        register={register}
                        getValue={getValue}
                        getValues={getValues}
                        reset={reset}
                        trigger={trigger}
                     />
                     <InvoiceCreateFormAddressSection
                        register={register}
                        countryFieldDisabled
                        countryIsoCode={country?.getIsoCode()}
                     />
                  </>
               ),
               visitPrivateIndividual: () => (
                  <>
                     <InvoiceCreateFormPrivateIndividualSection register={register} />
                     <InvoiceCreateFormAddressSection register={register} countryIsoCode={country?.getIsoCode()} />
                  </>
               ),
            })}
            <InvoiceCreateFormServicesSection
               invoiceItems={invoiceItems}
               register={register}
               taxRates={taxRates}
               visitDate={visitDate}
               visitSettlement={visitSettlement}
               receiptAvailableForInvoice={receiptAvailableForInvoice}
            />
         </StyledFlex>
         {children({
            formState: formState,
            handleSubmit: handleSubmit,
         })}
      </Form>
   );
};
